import { useQuery } from "@tanstack/react-query";
import { AgendaItemStatusEnum } from "enums/AgendaItemStatusEnum";
import { AgendaItemTypeEnum } from "enums/AgendaItemTypeEnum";
import { createLogger } from "logging";
import { PatchAgendaItemModel } from "models/PatchAgendaItemModel";
import { useAgendaItem, useApi, useMeeting, usePermissions, useToasts, useTranslations } from "providers";
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { EditAgendaItemPanel } from "./components/EditAgendaItemPanel";
import { AgendaItemFieldValues, EditAgendaItemContext, getAgendaItemFieldValues } from "./EditAgendaItemContext";

const logger = createLogger("EditAgendaItemProvider");

export const EditAgendaItemProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { agendaItemsApi } = useApi();
  const { reloadMeeting } = useMeeting();
  const { setAgendaItemUpdating } = useAgendaItem();
  const i18n = useTranslations();
  const toasts = useToasts();

  const [confirmUnsaved, setConfirmUnsaved] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [editAgendaItemId, setEditAgendaItemId] = useState<number>(-1);

  const form = useForm<AgendaItemFieldValues>({
    defaultValues: undefined,
  });
  const { control, formState, getValues, reset } = form;

  // Load agenda item using React Query
  const queryResult = useQuery({
    queryKey: [`AgendaItem|${editAgendaItemId}|Edit`],
    queryFn: async () => {
      const data = await agendaItemsApi.getItem(editAgendaItemId);
      const fieldValues = getAgendaItemFieldValues(data);
      reset(fieldValues);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: editAgendaItemId > 0,
    refetchOnWindowFocus: false,
  });
  const { error } = queryResult;
  const isLoading = queryResult.isFetching;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const watchErrors = JSON.stringify(formState.errors, null, 2);
  const { isDirty, isValid } = formState;
  const isSubmittable = useMemo(
    () => (!!editAgendaItemId && isValid) || watchErrors == "{}",
    [editAgendaItemId, isValid, watchErrors]
  );

  const handleClose = useCallback(
    (ignoreDirty?: boolean) => {
      const shouldConfirm = isDirty && !ignoreDirty;
      logger.debug(`handleClose(): isDirty:=${isDirty} (ignore:=${ignoreDirty}) -> shouldConfirm:=${shouldConfirm}`);
      if (shouldConfirm) {
        setConfirmUnsaved(true);
      } else {
        setIsSubmitting(false);
        setEditAgendaItemId(-1);
      }
    },
    [isDirty, setEditAgendaItemId]
  );

  const handleSuccess = useCallback(
    (title: string) => {
      toasts.updateSuccess(i18n.AgendaItem, title);
      setIsSubmitting(false);
      setEditAgendaItemId(-1);
      reloadMeeting();
    },
    [i18n, toasts, reloadMeeting]
  );

  const handleError = useCallback(
    (title: string, error: unknown) => {
      toasts.updateFailure(i18n.AgendaItem, title, error);
      throw error;
    },
    [i18n, toasts]
  );

  const getPatch = useCallback(
    (values: AgendaItemFieldValues) => {
      const {
        title,
        duration,
        status,
        type,
        confidential,
        locked,
        department,
        note,
        guests,
        finalBoard,
        decision,
        perusal,
        protocolEntryDraft,
        protocolEntry,
      } = values;

      const patch: PatchAgendaItemModel = {
        id: editAgendaItemId,
        title,
        duration,
        status: AgendaItemStatusEnum[status],
        type: AgendaItemTypeEnum[type],
        confidential,
        locked,
        department,
        note,
        guests,
        finalBoard,
        decision,
        perusal,
        protocolEntryDraft,
        protocolEntry,
      };
      return patch;
    },
    [editAgendaItemId]
  );

  const handleSave = useCallback(async () => {
    logger.debug(`handleSave(): `, editAgendaItemId, isSubmittable);
    if (!editAgendaItemId) return;
    if (!isSubmittable) return;

    try {
      setIsSubmitting(true);
      setAgendaItemUpdating(editAgendaItemId, true);

      const patch = getPatch(getValues());
      const { title = "" } = patch;

      try {
        await agendaItemsApi.update(patch);
        handleSuccess(title);
      } catch (error) {
        handleError(title, error);
      }

      handleClose(true);
    } catch (error) {
      logger.error("Error saving agenda item:", error);
    } finally {
      setIsSubmitting(false);
      setAgendaItemUpdating(editAgendaItemId, false);
    }
  }, [
    isSubmittable,
    editAgendaItemId,
    setAgendaItemUpdating,
    handleClose,
    agendaItemsApi,
    handleSuccess,
    handleError,
    getValues,
    getPatch,
  ]);

  const contextValue = useMemo(
    () => ({
      confirmUnsaved,
      setConfirmUnsaved,
      editAgendaItemId,
      setEditAgendaItemId,
      isLoading,
      isDirty,
      isValid,
      isSubmittable,
      isSubmitting,
      control,
      form,
      getPatch,
      handleSave,
      handleClose,
    }),
    [
      confirmUnsaved,
      editAgendaItemId,
      isLoading,
      isDirty,
      isValid,
      isSubmittable,
      isSubmitting,
      control,
      form,
      getPatch,
      handleSave,
      handleClose,
    ]
  );

  return (
    <EditAgendaItemContext.Provider value={contextValue}>
      {children}
      {editAgendaItemId !== -1 && <EditAgendaItemPanel />}
    </EditAgendaItemContext.Provider>
  );
};
